import { Component, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { NotifierService } from "angular-notifier";
import { FormField } from "src/app/interfaces/form-field.model";
import {
  faFloppyDisk,
  faEraser,
} from "@fortawesome/free-solid-svg-icons";
import {
  UpdateVehicleRateService,
  VehicleRateModel,
} from "src/app/services/autopac/update-vehicle-rate.service";

@Component({
  selector: "app-update-vehicle-rate",
  templateUrl: "./update-vehicle-rate.component.html",
  styleUrls: ["./update-vehicle-rate.component.css"],
})
export class UpdateVehicleRateComponent implements OnDestroy {
  faFloppyDisk = faFloppyDisk;
  faEraser = faEraser;
  loading = false;
  saving = false;
  message = "";
  private searchRequest?: Subscription;
  private updateRequest?: Subscription;
  tableHeaders = [
    { title: "CASE SERIES", type: "data", id: "CaseSeries" },
    { title: "CASE NUMBER", type: "data", id: "CaseNumber" },
    { title: "ENGINE NUMBER", type: "data", id: "EngineNumber" },
    { title: "CHASSIS NUMBER", type: "data", id: "ChasisNumber" },
    { title: "VEHICLE RATE", type: "data", id: "VehicleRate" },
    { title: "ACTION", type: "edit", id: "edit" },
  ];
  filteredData: VehicleRateModel[] = [];
  selectedRow: VehicleRateModel | null = null;
  searchBooking: FormField[] = [
    {
      type: "search",
      id: "caseSeries",
      label: "Case Series",
      searchEvent: true,
    },
  ];
  buyersDetails: FormField[] = [
    {
      type: "text",
      id: "CaseSeries",
      label: "Case Series",
      lock: true,
    },
    {
      type: "text",
      id: "CaseNumber",
      label: "Case Number",
      lock: true,
    },
    {
      type: "text",
      id: "EngineNumber",
      label: "Engine Number",
      lock: true,
    },
    {
      type: "text",
      id: "ChasisNumber",
      label: "Chassis Number",
      lock: true,
    },
    { type: "text", id: "VehicleRate", label: "Vehicle Rate" },
  ];

  constructor(
    private service: UpdateVehicleRateService,
    private notifier: NotifierService,
  ) {}

  updateFields(event: { name: string; fields: FormField[] }): void {
    if (event.name === "searchBooking")
      this.searchBooking = event.fields;
    if (event.name === "buyersDetails")
      this.buyersDetails = event.fields;
  }

  searchCaseSeries(): void {
    if (this.saving) return;
    this.searchRequest?.unsubscribe();
    this.loading = false;
    this.filteredData = [];
    this.clearVehicleRateForm();
    this.message = "";
    const series = String(this.searchBooking[0].value ?? "").trim();
    if (!series) {
      this.notifier.notify("error", "Please enter a case series.");
      return;
    }
    this.loading = true;
    this.searchRequest = this.service
      .getVehicleRateList(series)
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (
            response.statusCode !== 200 ||
            !Array.isArray(response.body)
          ) {
            this.message = "Unable to retrieve vehicle rates.";
            this.notifier.notify(
              "error",
              response.message || this.message,
            );
            return;
          }
          // Keep the backend's strings/nulls, just as V1 does when selecting a row.
          this.filteredData = response.body;
          this.message = this.filteredData.length
            ? ""
            : "No vehicle rates found for this case series.";
        },
        error: () => {
          this.loading = false;
          this.message = "Unable to retrieve vehicle rates.";
          this.notifier.notify("error", this.message);
        },
      });
  }

  editVehicleRate(entry: VehicleRateModel): void {
    if (this.saving || !entry) return;
    this.selectedRow = entry;
    this.buyersDetails = this.buyersDetails.map((field) => ({
      ...field,
      value: entry[field.id as keyof VehicleRateModel],
    }));
  }

  clearVehicleRateForm(): void {
    if (this.saving) return;
    this.selectedRow = null;
    this.buyersDetails = this.buyersDetails.map((field) => ({
      ...field,
      value: "",
    }));
  }

  updateVehicleRate(): void {
    if (this.saving) return;
    const row = this.selectedRow;
    if (
      !row ||
      !row.CaseSeries ||
      !row.CaseNumber ||
      !row.EngineNumber ||
      !row.ChasisNumber
    ) {
      this.notifier.notify(
        "error",
        "Please select a vehicle with a case series, case number, engine number and chassis number.",
      );
      return;
    }
    const rate = this.buyersDetails.find(
      (field) => field.id === "VehicleRate",
    )?.value;
    const model: VehicleRateModel = {
      __type: "KAPS.Model.Autopac.CrmVehicleRate",
      CaseSeries: row.CaseSeries,
      CaseNumber: row.CaseNumber,
      EngineNumber: row.EngineNumber,
      ChasisNumber: row.ChasisNumber,
      // The ASMX DTO is a string: preserve empty strings, null and "0" distinctly.
      VehicleRate: rate == null ? null : String(rate),
    };
    this.saving = true;
    this.updateRequest = this.service
      .updateVehicleRate({ model })
      .subscribe({
        next: (response) => {
          this.saving = false;
          if (response.code !== 0) {
            this.notifier.notify(
              "error",
              response.message || "Unable to update vehicle rate.",
            );
            return;
          }
          this.filteredData = this.filteredData.map((item) =>
            item === row
              ? { ...item, VehicleRate: model.VehicleRate }
              : item,
          );
          this.clearVehicleRateForm();
          this.notifier.notify(
            "success",
            "Vehicle Rate updated successfully.",
          );
        },
        error: () => {
          this.saving = false;
          this.notifier.notify(
            "error",
            "Unable to update vehicle rate.",
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.searchRequest?.unsubscribe();
    this.updateRequest?.unsubscribe();
  }
}
